"use client";

import { useSupabaseClient, useUser } from "@/components/SupabaseProvider";
import { ArrowRightStartOnRectangleIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import Link from "next/link";
import { usePostHog } from "posthog-js/react";
import { ComponentPropsWithoutRef, FC } from "react";
import { useMessages } from "../context/i18n";
import { useIsMarketingPage } from "../context/marketingPageContext";
import { nav as enNav } from "../dictionaries/en/nav";
import { AuthHelper } from "../helpers/auth_helper";
import Button from "./Button";

type LoginButtonProps = {} & ComponentPropsWithoutRef<"div">;

const LoginButton: FC<LoginButtonProps> = ({ ...rest }) => {
  const user = useUser();
  const isMarketingPage = useIsMarketingPage();
  const supabaseClient = useSupabaseClient();
  const queryClient = useQueryClient();
  const posthog = usePostHog();
  const m = useMessages("nav", enNav);

  const signOut = async () => {
    posthog?.reset();
    AuthHelper.removeAllCookies();

    // Local: the default "global" scope also ends every other device's session
    // (teams share logins), stranding their open tabs on klap-api 401s.
    await supabaseClient.auth.signOut({ scope: "local" });

    queryClient.clear();
  };

  // Marketing pages prerender signed-out, so the "Log in / Get started" pair
  // is what paints first; once the session resolves, a signed-in visitor
  // gets one button into the app instead of two that both bounce there.
  return (
    <div {...rest} className={`${rest.className} flex gap-2`}>
      {user && isMarketingPage ? (
        <Link href="/spaces">
          <Button size="m" onClick={() => {}} className="w-full">
            {m.dashboard}
          </Button>
        </Link>
      ) : user ? (
        <Button
          size="m"
          onClick={signOut}
          variant="tertiary"
          align="start"
          className="w-full flex hover:!bg-slate-100"
        >
          <ArrowRightStartOnRectangleIcon className="w-4 h-4" /> Log out
        </Button>
      ) : (
        <>
          <Link href="/login">
            <Button
              size="m"
              onClick={() => {}}
              className="w-full"
              variant="tertiary"
            >
              {m.logIn}
            </Button>
          </Link>
          <Link href="/signup">
            <Button size="m" onClick={() => {}} className="w-full">
              {m.getStarted}
            </Button>
          </Link>
        </>
      )}
    </div>
  );
};

export default LoginButton;
